import { useMemo } from "react";
import { STATE_PILLAR_MATRIX } from "../../data/ybf/statePillarSplit";
import { PILLARS } from "../../data/ybf/pillars";
import { fmtInt } from "../../data/ybf/utils";

const PALETTE = [
  "#F3F4F6", "#FEE2E2", "#FECACA", "#FCA5A5", "#F87171",
  "#EF4444", "#E2231A", "#B91C1C", "#7F1D1D",
];

function colorFor(value, max) {
  if (!value) return "#F8FAFC";
  const ratio = Math.min(value / max, 1);
  const idx = Math.min(Math.floor(ratio * PALETTE.length), PALETTE.length - 1);
  return PALETTE[idx];
}

function textOnFill(value, max) {
  if (!value) return "#94A3B8";
  return value / max > 0.45 ? "#FFFFFF" : "#0F172A";
}

export default function PillarStateHeatmap() {
  // All 18 states, sorted by total beneficiaries (largest at top)
  const rows = useMemo(
    () => [...STATE_PILLAR_MATRIX].sort((a, b) => b.total - a.total),
    []
  );

  const max = useMemo(
    () => Math.max(...rows.flatMap((s) => PILLARS.map((p) => s[p.id]))),
    [rows]
  );

  // Column totals (per pillar across all states shown)
  const colTotals = useMemo(() => {
    const t = {};
    PILLARS.forEach((p) => {
      t[p.id] = rows.reduce((sum, s) => sum + (s[p.id] || 0), 0);
    });
    return t;
  }, [rows]);

  const grandTotal = rows.reduce((s, x) => s + x.total, 0);

  return (
    <div
      data-testid="pillar-state-heatmap"
      className="bg-white rounded-2xl border border-slate-200 p-4"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900">State × Pillar Heatmap</h3>
          <p className="text-[11px] text-slate-500">
            Beneficiaries by pillar across all {rows.length} states. Darker = more reach.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Total</div>
          <div className="text-base font-bold text-[#1A1A1A]">{fmtInt(grandTotal)}</div>
        </div>
      </div>

      {/* Grid */}
      <div
        className="grid gap-1.5"
        style={{
          gridTemplateColumns: `minmax(140px,auto) repeat(${PILLARS.length}, minmax(90px,1fr)) minmax(80px,auto)`,
        }}
      >
        {/* Header row */}
        <div />
        {PILLARS.map((p) => (
          <div key={p.id} className="flex items-center justify-center gap-1.5 pb-1">
            <span className="w-1.5 h-3 rounded-full" style={{ background: p.color }} />
            <div className="text-center leading-tight">
              <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: p.color }}>
                {p.short}
              </div>
              <div className="text-[9px] uppercase text-slate-500 font-medium">{p.name.split(" ")[0]}</div>
            </div>
          </div>
        ))}
        <div className="text-right text-[10px] font-bold uppercase tracking-wider text-slate-500 pb-1 pr-1 self-end">
          Total
        </div>

        {/* State rows */}
        {rows.map((s) => (
          <div key={s.state} className="contents">
            {/* State chip */}
            <div className="flex items-center px-1">
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-slate-800 text-[11px] font-semibold border border-slate-200 max-w-full truncate"
                title={s.state}
                data-testid={`state-chip-${s.state.replace(/\s+/g, "")}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A] flex-shrink-0" />
                <span className="truncate">{s.state}</span>
              </span>
            </div>

            {/* Pillar cells */}
            {PILLARS.map((p) => {
              const v = s[p.id] || 0;
              return (
                <div
                  key={s.state + p.id}
                  data-testid={`pillar-state-cell-${p.id}-${s.state.replace(/\s+/g, "")}`}
                  className="rounded-lg flex items-center justify-center font-bold text-[11px] transition-transform hover:scale-[1.04] cursor-default"
                  style={{
                    background: colorFor(v, max),
                    color: textOnFill(v, max),
                    minHeight: 32,
                  }}
                  title={`${p.name} · ${s.state}: ${v.toLocaleString("en-IN")} beneficiaries`}
                >
                  {v ? fmtInt(v) : "—"}
                </div>
              );
            })}

            {/* State total */}
            <div className="flex items-center justify-end pr-1">
              <span className="text-[11px] font-bold text-slate-800 bg-slate-50 px-2 py-0.5 rounded-md">
                {fmtInt(s.total)}
              </span>
            </div>
          </div>
        ))}

        {/* Footer totals row */}
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold pt-2 pl-1">
          Pillar total
        </div>
        {PILLARS.map((p) => (
          <div key={"total-" + p.id} className="flex justify-center pt-2">
            <span
              className="text-[11px] font-bold px-2 py-0.5 rounded-md"
              style={{ background: `${p.color}10`, color: p.color }}
            >
              {fmtInt(colTotals[p.id])}
            </span>
          </div>
        ))}
        <div />
      </div>

      {/* Legend */}
      <div className="flex items-center gap-2 mt-4 px-1">
        <span className="text-[10px] text-slate-500 font-medium">Fewer</span>
        <div className="flex flex-1 h-2 rounded-full overflow-hidden">
          {PALETTE.map((c) => (
            <div key={c} className="flex-1 h-full" style={{ background: c }} />
          ))}
        </div>
        <span className="text-[10px] text-slate-500 font-medium">More</span>
        <span className="text-[10px] text-slate-400 ml-2">(max {fmtInt(max)})</span>
      </div>
    </div>
  );
}
